import React, { useState, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { ShieldCheck } from 'lucide-react';

import useAuditLogApi, { auditExportLabels } from './config';
import AuditLogDetailDrawer from './AuditLogDetailDrawer';
import { AUDIT_LOG_EXPORT_URL, verifyAuditChain } from '../../services/auditLogService';
import { parseTableParams } from '../../utils/Utils';
import { buildReportLabels } from '../../utils/reportLabels';
import DataTable from '../../components/ui/DataTable';
import Button from '../../components/ui/Button';
import ReportExportButtons from '../../components/ui/ReportExportButtons';
import GenericFormDrawer from '../../components/GenericFormDrawer';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 0;
`;

const ErrorNote = styled.div`
  background: #FEF2F2;
  border: 1px solid #FECACA;
  color: #991B1B;
  border-radius: 6px;
  padding: 10px 12px;
  font-size: 13px;
  margin-bottom: 12px;
`;

const Toolbar = styled.span`
  display: inline-flex;
  flex-wrap: wrap;
  gap: 8px;
  vertical-align: middle;
`;

// Doğrulama aralığı = listenin tarih süzgeçleri AYNEN (sunucu ikisini de kiracının yerel günüyle, son gün dahil çözer).
// Tarih süzgeci yoksa boş: sunucu bugünü doğrular (varsayılan aralık halka tavanının içinde kalır).
export function verificationRange(params) {
  const out = {};
  ['occurredAt[between]', 'occurredAt[gte]', 'occurredAt[lte]'].forEach((key) => {
    if (params?.[key]) out[key] = params[key];
  });
  return out;
}

/**
 * AKM POC §9.1 — İşlem Geçmişi (yalnız şirket yöneticisi). EmployeesPage aynası: sunucu sayfalı DataTable, satır →
 * salt-okunur ayrıntı çekmecesi. Dışa aktarma sunucudan (Excel / PDF, olay runner'dan); yerleşik CSV yok.
 */
export default function AuditLogsPage() {
  const { t } = useTranslation();
  const [queryParams, setQueryParams] = useState({ pageSize: 20, pageNumber: 1 });
  const [filterList, setFilterList] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [verification, setVerification] = useState(null);
  const [verifying, setVerifying] = useState(false);

  const { columns, data, fetching, loadFailed, pagination, entityTypes, labels } = useAuditLogApi(queryParams);

  const handleDataStateChange = useCallback((action, tableState) => {
    setFilterList(tableState.filterList);
    setQueryParams(parseTableParams(tableState, columns));
  }, [columns]);

  const handleFilterConfirm = useCallback((next) => setFilterList(next), []);

  const handleRowClick = useCallback((row) => setSelectedId(row.id), []);

  const handleVerify = useCallback(async () => {
    setVerifying(true);
    try {
      const res = await verifyAuditChain(verificationRange(queryParams));
      setVerification(res?.data?.status === false || !res?.data ? { failed: true } : res.data.data);
    } finally {
      setVerifying(false);
    }
  }, [queryParams]);

  const getExportLabels = useCallback(
    () => buildReportLabels(t, columns, t('auditLog.title'), auditExportLabels(t, labels, entityTypes)),
    [t, columns, labels, entityTypes],
  );

  const toolbarActions = useMemo(() => (
    <Toolbar>
      <Button type="button" variant="outline" data-action="verify-chain" loading={verifying} onClick={handleVerify}>
        <ShieldCheck size={16} aria-hidden="true" />
        {t('auditLog.chain.verify')}
      </Button>
      <ReportExportButtons url={AUDIT_LOG_EXPORT_URL} params={queryParams} getLabels={getExportLabels} />
    </Toolbar>
  ), [t, verifying, handleVerify, queryParams, getExportLabels]);

  const verificationContent = useMemo(() => {
    if (!verification) return null;
    if (verification.failed) return t('auditLog.loadFailed');
    return (
      <>
        {verification.verified
          ? t('auditLog.chain.verified', { count: verification.checkedCount ?? 0 })
          : t('auditLog.chain.broken', { seq: verification.brokenAtSeq ?? '-' })}
        {verification.unsealedCount > 0 && (
          <ul>
            <li>{t('auditLog.chain.unsealed', { count: verification.unsealedCount })}</li>
          </ul>
        )}
      </>
    );
  }, [verification, t]);

  return (
    <Container>
      <AuditLogDetailDrawer logId={selectedId} onClose={() => setSelectedId(null)} />

      <GenericFormDrawer
        open={Boolean(verification)}
        onClose={() => setVerification(null)}
        title={t('auditLog.chain.verify')}
        schema={verification ? [{
          name: '_notice',
          type: 'notice',
          grid: 12,
          tone: verification.verified ? undefined : 'warning',
          content: verificationContent,
        }] : []}
        initialValues={verification ? {} : null}
        onSave={async () => true}
        submitLabel={t('common.close')}
      />

      {loadFailed && <ErrorNote role="alert">{t('auditLog.loadFailed')}</ErrorNote>}

      {useMemo(() => (
        <DataTable
          title={t('auditLog.title')}
          fetching={fetching}
          columns={columns}
          data={data}
          totalRowCount={pagination.totalRowCount}
          pageIndex={pagination.pageIndex}
          pageSize={pagination.pageSize}
          filterList={filterList}
          onFilterConfirm={handleFilterConfirm}
          onTableChanged={handleDataStateChange}
          onRowClick={handleRowClick}
          toolbarActions={toolbarActions}
          download={false}
        />
      ), [t, fetching, columns, data, pagination, filterList, handleFilterConfirm, handleDataStateChange, handleRowClick, toolbarActions])}
    </Container>
  );
}
